
/**
 * Objective Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class ObjectiveService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            // BaseApi.getAll returns { success, data, pagination, uniqueValues }
            return {
                success: true,
                data: response.data,
                pagination: response.pagination || response.meta || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'Objective listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch objective list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch objective');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Objective oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create objective');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Objective güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update objective');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Objective silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete objective');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }

    /**
     * Get paginated related risks for an Objective
     * @param {string} id - Objective ID
     * @param {Object} params - Query parameters (page, limit, search, sort, filter)
     */
    async getRelatedRisks(id, params = {}) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/${id}/risks`, { params });
            const res = response.data || response;
            return {
                success: true,
                data: res.data,
                meta: res.meta || {},
                message: res.message || 'Risk listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related risks for objective');
        }
    }

    /**
     * Get paginated related treatments for an Objective
     * @param {string} id - Objective ID
     * @param {Object} params - Query parameters (page, limit, search, sort)
     */
    async getRelatedTreatments(id, params) {
        try {
            const response = await this.api.getRelatedTreatments(id, params);
            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message || 'Aksiyon listesi getirildi'
            };
        } catch (error) {
            this._handleError(error, 'Failed to fetch related treatments for objective');
        }
    }

    /**
     * Link treatments to objective
     * @param {string} id - Objective ID
     * @param {Array<string>} treatmentIds - Array of Treatment IDs
     */
    async addTreatments(id, treatmentIds) {
        try {
            const response = await this.api.addTreatments(id, treatmentIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Aksiyonlar başarıyla eklendi'
            };
        } catch (error) {
            this._handleError(error, 'Failed to link treatments to objective');
        }
    }

    /**
     * Unlink treatment from objective
     * @param {string} id - Objective ID
     * @param {string} treatmentId - Treatment ID
     */
    async removeTreatment(id, treatmentId) {
        try {
            const response = await this.api.removeTreatment(id, treatmentId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Aksiyon başarıyla kaldırıldı'
            };
        } catch (error) {
            this._handleError(error, 'Failed to unlink treatment from objective');
        }
    }

    /**
     * Link Risks to Objective
     * @param {string} id - Objective ID
     * @param {Array<string>} riskIds - Array of Risk IDs to link
     */
    async addRisks(id, riskIds) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/risks`, { riskIds });
            const res = response.data || response;
            res.success = res.success !== false;
            return res;
        } catch (error) {
            throw this._handleError(error, 'Failed to link risks to objective');
        }
    }

    /**
     * Unlink Risk from Objective
     * @param {string} id - Objective ID
     * @param {string} riskId - Risk ID to unlink
     */
    async removeRisk(id, riskId) {
        try {
            const response = await this.api.client.delete(`${this.api.basePath}/${id}/risks/${riskId}`);
            const res = response.data || response;
            res.success = res.success !== false;
            return res;
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink risk from objective');
        }
    }

    /**
     * Get paginated related KPIs for an Objective
     * @param {string} id - Objective ID
     * @param {Object} params - Query parameters (page, limit, search, sort)
     */
    async getRelatedKpis(id, params) {
        try {
            const response = await this.api.getRelatedKpis(id, params);
            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message || 'KPI listesi getirildi'
            };
        } catch (error) {
            this._handleError(error, 'Failed to fetch related KPIs for objective');
        }
    }

    /**
     * Link KPIs to objective
     * @param {string} id - Objective ID
     * @param {Array<string>} kpiIds - Array of KPI IDs
     */
    async addKpis(id, kpiIds) {
        try {
            const response = await this.api.addKpis(id, kpiIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'KPI\'lar başarıyla eklendi'
            };
        } catch (error) {
            this._handleError(error, 'Failed to link KPIs to objective');
        }
    }

    /**
     * Unlink KPI from objective
     * @param {string} id - Objective ID
     * @param {string} kpiId - KPI ID
     */
    async removeKpi(id, kpiId) {
        try {
            const response = await this.api.removeKpi(id, kpiId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'KPI başarıyla kaldırıldı'
            };
        } catch (error) {
            this._handleError(error, 'Failed to unlink KPI from objective');
        }
    }

    /**
     * Get paginated related Strategic Initiatives for an Objective
     * @param {string} id - Objective ID
     * @param {Object} params - Query parameters (page, limit, search, sort)
     */
    async getRelatedInitiatives(id, params) {
        try {
            const response = await this.api.getRelatedInitiatives(id, params);
            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message || 'Stratejik İnisiyatif listesi getirildi'
            };
        } catch (error) {
            this._handleError(error, 'Failed to fetch related Strategic Initiatives for objective');
        }
    }

    /**
     * Link Strategic Initiatives to objective
     * @param {string} id - Objective ID
     * @param {Array<string>} initiativeIds - Array of Initiative IDs
     */
    async addInitiatives(id, initiativeIds) {
        try {
            const response = await this.api.addInitiatives(id, initiativeIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Stratejik İnisiyatifler başarıyla eklendi'
            };
        } catch (error) {
            this._handleError(error, 'Failed to link Strategic Initiatives to objective');
        }
    }

    /**
     * Unlink Strategic Initiative from objective
     * @param {string} id - Objective ID
     * @param {string} initiativeId - Initiative ID
     */
    async removeInitiative(id, initiativeId) {
        try {
            const response = await this.api.removeInitiative(id, initiativeId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Stratejik İnisiyatif başarıyla kaldırıldı'
            };
        } catch (error) {
            this._handleError(error, 'Failed to unlink Strategic Initiative from objective');
        }
    }
}
